// Case Buddy full report package helpers.
// The one-click package is assembled from current spreadsheet-shaped case data,
// reviewed deliverable state, and generated report/addendum sections. It does
// not replace Main Data, Raw Data, or the Output/Review buffer as case truth.
import {
  WORKFLOW_SIGNAL_CASE_FLOW_VISUALS_CREATED,
  WORKFLOW_SIGNAL_FULL_REPORT_PACKAGE_REVIEWED
} from './CaseBuddyAppConstants.js';
import { buildTsv, caseBuddyHashString, normalizeCell, normalizeComparisonKey, safeDateLabel } from './CaseBuddyShared.js';
import {
  buildCaseFlowVisual,
  CASE_FLOW_VISUALS_DEFAULT_OPTIONS,
  CASE_FLOW_VISUALS_RENDERER_VERSION,
  serializeCaseFlowVisualSvg
} from './CaseBuddyFlowVisuals.js';

export const CASE_FLOW_VISUALS_PLACEHOLDER_RENDERER_VERSION = 'case_flow_visuals_tool87_package_v1_web';
export const CASE_FULL_REPORT_PACKAGE_RENDERER_VERSION = 'case_full_report_package_renderer_v5_web';
export const CASE_FULL_REPORT_PACKAGE_PREVIEW_LIMIT = 5000;

export const CASE_PACKAGE_WWW_STABLE_TOKEN_SET = Object.freeze(new Set(['DAI', 'USDT', 'USDC', 'USDD', 'USD']));

export const CASE_PACKAGE_WWW_ENTITY_TABLE_CATEGORIES = Object.freeze(['Exchange']);

export const CASE_PACKAGE_WWW_OTHER_SUMMARY_CATEGORIES = Object.freeze(['Non-Exchange', 'Other', 'Lost/Dismissed']);

export function normalizeCasePackageLineBreaks(value = '') {
  return value == null ? '' : String(value).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

export function casePackageHtmlEncode(value = '') {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function normalizeEscapedCasePackageCellBreaks(value = '') {
  if (value == null) return '';
  return String(value)
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n')
    .replace(/&#13;&#10;/g, '\n')
    .replace(/&#10;/g, '\n')
    .replace(/&#13;/g, '\n');
}

export function casePackagePlainTextToHtml(text = '') {
  const safe = normalizeCasePackageLineBreaks(text).trim() || 'Not available.';
  return safe
    .split(/\n{2,}/)
    .map(block => block.trim())
    .filter(Boolean)
    .map(block => `<p>${casePackageHtmlEncode(block).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

export function casePackageRowsToTsv(rows = []) {
  return buildTsv(Array.isArray(rows) ? rows : []);
}

export function casePackageHasMeaningfulTsvRows(tsv = '') {
  const safe = normalizeCasePackageLineBreaks(tsv).trim();
  if (!safe || !safe.includes('\t')) return false;
  const lines = safe.split('\n').filter(line => line.trim());
  if (lines.length <= 1) return false;
  return lines.slice(1).some((line) => line.split('\t').some(cell => normalizeEscapedCasePackageCellBreaks(cell).trim()));
}

export function keepFirstCasePackageTsvColumns(tsv = '', maxColumns = 2) {
  const safe = normalizeCasePackageLineBreaks(tsv).trim();
  if (!safe || !safe.includes('\t') || maxColumns <= 0) return safe;
  return safe
    .split('\n')
    .filter(line => line.trim())
    .map((line) => line.split('\t').slice(0, maxColumns).join('\t'))
    .join('\n');
}

export function casePackageTsvToHtmlTable(tsv = '') {
  const lines = normalizeCasePackageLineBreaks(tsv).split('\n').filter(line => line.trim());
  if (!lines.length) return casePackagePlainTextToHtml('Not available.');
  let wroteHeader = false;
  const rowHtml = lines.map((line) => {
    const cells = line.split('\t');
    const tag = wroteHeader ? 'td' : 'th';
    wroteHeader = true;
    return `<tr>${cells.map(cell => `<${tag}>${casePackageHtmlEncode(normalizeEscapedCasePackageCellBreaks(cell)).replace(/\n/g, '<br>')}</${tag}>`).join('')}</tr>`;
  }).join('');
  return `<table>${rowHtml}</table>`;
}

export function buildCasePackageHtmlDocument(htmlFragment = '') {
  return '<!DOCTYPE html><html><head><meta charset="utf-8">' +
    '<style>' +
    'body{font-family:Arial,Helvetica,sans-serif;font-size:11pt;color:#000;}' +
    'h1{font-size:18pt;margin:0 0 18px 0;}' +
    'h2{font-size:14pt;margin:24px 0 8px 0;border-bottom:1px solid #999;padding-bottom:3px;}' +
    'p{margin:0 0 8px 0;}table{border-collapse:collapse;width:100%;margin:8px 0 16px 0;}' +
    'th,td{border:1px solid #999;padding:4px;vertical-align:top;}th{font-weight:bold;background:#f3f4f6;}' +
    'h3{font-size:12pt;margin:12px 0 6px 0;}' +
    '.case-package-www-summary-table{border-collapse:collapse;width:auto;min-width:5.75in;margin:8px 0 10px 0;}' +
    '.case-package-www-summary-table th,.case-package-www-summary-table td{border:0;padding:3px 18px 3px 0;text-align:left;vertical-align:top;background:transparent;}' +
    '.case-package-www-summary-table th{font-weight:bold;border-bottom:1px solid #999;}' +
    '.case-package-www-summary-table td:nth-child(2),.case-package-www-summary-table th:nth-child(2){text-align:right;white-space:nowrap;}' +
    '.case-package-www-summary-table td:nth-child(3),.case-package-www-summary-table th:nth-child(3){white-space:nowrap;}' +
    '.case-package-www-last-entity-row td{border-bottom:1px solid #000;}' +
    '.case-package-www-total-row td{font-weight:bold;padding-top:5px;}' +
    '.case-package-www-summary-note{margin-top:8px;}' +
    '.case-report-path-heading--page-break{page-break-before:always;break-before:page;mso-page-break-before:always;}' +
    '.case-package-flow-figure{max-width:7.25in;margin:10px 0 18px 0;page-break-inside:avoid;break-inside:avoid;}' +
    '.case-package-flow-figure svg,.case-package-flow-figure img{max-width:100%;height:auto;display:block;}' +
    '</style></head><body><!--StartFragment-->' +
    String(htmlFragment || '') +
    '<!--EndFragment--></body></html>';
}

export function casePackageNormalizeVisualOptions(options = {}) {
  return {
    ...CASE_FLOW_VISUALS_DEFAULT_OPTIONS,
    ...(options || {})
  };
}

function casePackageParseNumber(value, fallback = 0) {
  const parsed = Number(String(value ?? '').replace(/[^0-9.+-]/g, ''));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function casePackageGetSvgRenderSize(svgElement, options = {}) {
  if (!svgElement || typeof svgElement.getAttribute !== 'function') {
    return { width: Number(options.fallbackWidth) || 680, height: Number(options.fallbackHeight) || 390 };
  }
  const widthAttr = casePackageParseNumber(svgElement.getAttribute('width'), 0);
  const heightAttr = casePackageParseNumber(svgElement.getAttribute('height'), 0);
  const viewBox = String(svgElement.getAttribute('viewBox') || '').trim().split(/\s+/).map(Number).filter(Number.isFinite);
  const viewBoxWidth = viewBox.length >= 4 && viewBox[2] > 0 ? viewBox[2] : 0;
  const viewBoxHeight = viewBox.length >= 4 && viewBox[3] > 0 ? viewBox[3] : 0;
  const width = Math.max(320, Math.round(widthAttr || viewBoxWidth || Number(options.fallbackWidth) || 680));
  const rawHeight = heightAttr || (viewBoxWidth && viewBoxHeight ? (viewBoxHeight / viewBoxWidth) * width : 0) || Number(options.fallbackHeight) || 390;
  const height = Math.max(180, Math.round(rawHeight));
  return { width, height };
}

function casePackageLoadImageElement(src = '') {
  return new Promise((resolve, reject) => {
    if (typeof Image === 'undefined') {
      reject(new Error('Image constructor is not available.'));
      return;
    }
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not render SVG figure as an image.'));
    image.src = src;
  });
}

export async function casePackageSvgElementToPngDataUrl(svgElement, options = {}) {
  if (!svgElement || typeof document === 'undefined' || typeof XMLSerializer === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined') {
    return '';
  }
  const cloned = svgElement.cloneNode(true);
  if (typeof cloned.setAttribute === 'function' && !cloned.getAttribute('xmlns')) {
    cloned.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  }
  const { width, height } = casePackageGetSvgRenderSize(cloned, options);
  if (typeof cloned.setAttribute === 'function') {
    cloned.setAttribute('width', String(width));
    cloned.setAttribute('height', String(height));
  }
  const serializer = new XMLSerializer();
  const svgText = serializer.serializeToString(cloned);
  const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await casePackageLoadImageElement(objectUrl);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return '';
    const maxRasterWidth = Math.max(900, Number(options.maxRasterWidth) || 1800);
    const requestedScale = Math.max(1, Number(options.scale) || 2);
    const scale = Math.max(1, Math.min(requestedScale, maxRasterWidth / Math.max(1, width)));
    canvas.width = Math.max(1, Math.ceil(width * scale));
    canvas.height = Math.max(1, Math.ceil(height * scale));
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function casePackageConvertInlineSvgFiguresToPngImages(htmlDocument = '', options = {}) {
  const safeHtml = String(htmlDocument || '');
  if (!safeHtml) {
    return { htmlDocument: '', sourceVisualCount: 0, convertedVisualCount: 0, failedVisualCount: 0, usedRasterImages: false };
  }
  if (typeof DOMParser === 'undefined' || typeof document === 'undefined') {
    return { htmlDocument: safeHtml, sourceVisualCount: 0, convertedVisualCount: 0, failedVisualCount: 0, usedRasterImages: false, domUnavailable: true };
  }
  const parsed = new DOMParser().parseFromString(safeHtml, 'text/html');
  const figures = Array.from(parsed.querySelectorAll('.case-package-flow-figure'));
  const svgTargets = figures
    .map(figure => ({ figure, svg: figure.querySelector('svg') }))
    .filter(target => target.svg);
  let convertedVisualCount = 0;
  let failedVisualCount = 0;
  for (const { figure, svg } of svgTargets) {
    const title = normalizeCell(svg.getAttribute('aria-label') || svg.querySelector('title')?.textContent || 'Case flow visual');
    const { width, height } = casePackageGetSvgRenderSize(svg, options);
    try {
      const dataUrl = await casePackageSvgElementToPngDataUrl(svg, options);
      if (!dataUrl) throw new Error('Raster image conversion returned no output.');
      const image = parsed.createElement('img');
      image.setAttribute('src', dataUrl);
      image.setAttribute('alt', title);
      image.setAttribute('width', String(width));
      image.setAttribute('height', String(height));
      image.setAttribute('data-case-buddy-report-visual', 'png');
      image.setAttribute('style', 'width:100%;max-width:7.1in;height:auto;display:block;margin:0 auto;border:0;');
      svg.replaceWith(image);
      convertedVisualCount += 1;
    } catch {
      failedVisualCount += 1;
      if (options.replaceFailedSvgWithPlaceholder !== false) {
        figure.innerHTML = `<p style="margin:8px 0;font-style:italic;color:#374151;">${casePackageHtmlEncode(title)} is available in the downloaded case flow visual package.</p>`;
      }
    }
  }
  const bodyHtml = (parsed.body?.innerHTML || safeHtml)
    .replace(/<!--StartFragment-->/g, '')
    .replace(/<!--EndFragment-->/g, '');
  return {
    htmlDocument: buildCasePackageHtmlDocument(bodyHtml),
    sourceVisualCount: svgTargets.length,
    convertedVisualCount,
    failedVisualCount,
    usedRasterImages: convertedVisualCount > 0
  };
}

export async function buildCaseFullReportPackageClipboardHtml(payload = {}, options = {}) {
  const sourceHtml = payload.htmlDocument || buildCasePackageHtmlDocument(payload.htmlFragment || '');
  return casePackageConvertInlineSvgFiguresToPngImages(sourceHtml, {
    scale: 2,
    maxRasterWidth: 1800,
    replaceFailedSvgWithPlaceholder: true,
    ...(options || {})
  });
}


export function casePackageStripSvgXmlHeader(svgText = '') {
  return String(svgText || '').replace(/^\s*<\?xml[^>]*>\s*/i, '').trim();
}

export function casePackageScaleInlineSvg(svgText = '', visual = {}, options = {}) {
  const cleaned = casePackageStripSvgXmlHeader(svgText);
  if (!cleaned) return '';
  const targetWidth = Math.max(420, Math.min(760, Number(options.width) || 680));
  const box = visual?.viewBox || {};
  const rawWidth = Number(box.width) || targetWidth;
  const rawHeight = Number(box.height) || Math.round(targetWidth * 0.58);
  const targetHeight = Math.max(260, Math.min(620, Math.round((rawHeight / Math.max(1, rawWidth)) * targetWidth)));
  return cleaned.replace(/<svg\b([^>]*)>/i, (match, attrs = '') => {
    let nextAttrs = String(attrs || '')
      .replace(/\swidth="[^"]*"/gi, '')
      .replace(/\sheight="[^"]*"/gi, '')
      .replace(/\sstyle="[^"]*"/gi, '')
      .trim();
    if (!/\bxmlns=/.test(nextAttrs)) nextAttrs = `xmlns="http://www.w3.org/2000/svg" ${nextAttrs}`.trim();
    const style = 'width:100%;max-width:7.1in;height:auto;display:block;margin:0 auto;page-break-inside:avoid;';
    return `<svg ${nextAttrs} width="${targetWidth}" height="${targetHeight}" style="${style}">`;
  });
}

export function casePackageBuildEmbeddedVisualBlocks(visualsState = {}, options = {}) {
  const flows = Array.isArray(visualsState?.flows) ? visualsState.flows : [];
  if (!flows.length) return [];
  const visualOptions = casePackageNormalizeVisualOptions(options.visualOptions || options);
  return flows.map((flow, index) => {
    try {
      const visual = buildCaseFlowVisual(flow, visualsState, visualOptions);
      const svgText = serializeCaseFlowVisualSvg(visual, {
        title: `Case Buddy Flow ${flow.mainLabel || index + 1}`,
        description: 'Inline Case Buddy Tool87-style flow visual embedded in the one-click report package.'
      });
      const inlineSvg = casePackageScaleInlineSvg(svgText, visual, options);
      const label = normalizeCell(flow.mainLabel || visual.mainLabel || `Flow ${index + 1}`);
      const hash = normalizeCell(flow.mainHash || visual.mainHash || '');
      return {
        index,
        label,
        normalizedLabel: normalizeComparisonKey(label),
        hash,
        visual,
        html: inlineSvg
          ? '<div class="case-package-flow-figure" style="margin:10px 0 18px 0;page-break-inside:avoid;break-inside:avoid;max-width:7.25in;">' +
            `<div style="border:1px solid #d1d5db;border-radius:8px;padding:6px;background:#ffffff;overflow:hidden;">${inlineSvg}</div>` +
            '</div>'
          : '',
        plainText: `[Case flow visual for Transaction Path #${label || index + 1} is embedded in the rich package. Use the full visual download package for the full-size image.]`
      };
    } catch (error) {
      return {
        index,
        label: normalizeCell(flow.mainLabel || `Flow ${index + 1}`),
        normalizedLabel: normalizeComparisonKey(flow.mainLabel || `Flow ${index + 1}`),
        hash: normalizeCell(flow.mainHash || ''),
        html: '',
        plainText: `[Case flow visual for Transaction Path #${flow.mainLabel || index + 1} could not be embedded: ${normalizeCell(error?.message) || 'render error'}]`,
        error: normalizeCell(error?.message) || 'render error'
      };
    }
  });
}

export function casePackagePickVisualBlockForSection(section = {}, visualBlocks = [], usedIndexes = new Set()) {
  if (!Array.isArray(visualBlocks) || visualBlocks.length === 0) return null;
  const sectionLabel = normalizeComparisonKey(section?.pathLabel || section?.mainLabel || section?.key || '');
  if (sectionLabel) {
    const exact = visualBlocks.find(block => !usedIndexes.has(block.index) && block.normalizedLabel === sectionLabel && block.html);
    if (exact) {
      usedIndexes.add(exact.index);
      return exact;
    }
  }
  const fallback = visualBlocks.find(block => !usedIndexes.has(block.index) && block.html) || visualBlocks.find(block => block.html) || null;
  if (fallback) usedIndexes.add(fallback.index);
  return fallback || null;
}

export function casePackageReplaceFirstReportFigurePlaceholder(html = '', replacementHtml = '') {
  if (!html || !replacementHtml) return html;
  const pattern = /\(Add figure label name\)<br\s*\/?>\s*\(Insert figure here\)/i;
  return String(html).replace(pattern, () => replacementHtml);
}

export function casePackageReplaceFirstReportFigureTextPlaceholder(text = '', replacementText = '') {
  if (!text || !replacementText) return text;
  const pattern = /\(Add figure label name\)\n\(Insert figure here\)/i;
  return String(text).replace(pattern, () => replacementText);
}

export function buildCasePackageReportBodyWithEmbeddedVisuals(reportBodyState = {}, visualsState = {}, options = {}) {
  const basePlainText = normalizeCasePackageLineBreaks(reportBodyState?.generatedText || '').trim() || 'No current report body output is available.';
  const baseHtml = String(reportBodyState?.standardHtmlFragment || '');
  const sections = Array.isArray(reportBodyState?.document?.sections)
    ? reportBodyState.document.sections
    : Array.isArray(reportBodyState?.sections)
      ? reportBodyState.sections
      : [];
  if (options.embedFlowVisuals === false || !baseHtml || !sections.length || !visualsState?.canBuild) {
    return {
      plainText: basePlainText,
      htmlFragment: baseHtml,
      embeddedVisualCount: 0,
      missingVisualCount: sections.length,
      visualBlocks: []
    };
  }

  const visualBlocks = casePackageBuildEmbeddedVisualBlocks(visualsState, options);
  const used = new Set();
  let html = baseHtml;
  let plainText = basePlainText;
  let embeddedVisualCount = 0;
  let missingVisualCount = 0;
  sections.forEach((section, index) => {
    const block = casePackagePickVisualBlockForSection(section, visualBlocks, used, index);
    if (block?.html) {
      html = casePackageReplaceFirstReportFigurePlaceholder(html, block.html);
      plainText = casePackageReplaceFirstReportFigureTextPlaceholder(plainText, block.plainText);
      embeddedVisualCount += 1;
    } else {
      missingVisualCount += 1;
    }
  });
  return {
    plainText,
    htmlFragment: html,
    embeddedVisualCount,
    missingVisualCount,
    visualBlocks
  };
}

export function buildCasePackagePreviewText(plainText = '') {
  const safe = String(plainText || '');
  if (safe.length <= CASE_FULL_REPORT_PACKAGE_PREVIEW_LIMIT) return safe;
  return `${safe.slice(0, CASE_FULL_REPORT_PACKAGE_PREVIEW_LIMIT)}\n\n[Preview truncated. Copy or download the package to use the complete output.]`;
}

export function buildCaseFlowVisualsPlaceholderState(workflowState = {}, options = {}) {
  const revision = Number(options.caseDataRevision) || 0;
  const signals = workflowState?.signalCompleteByCode || {};
  const signalRecord = signals[WORKFLOW_SIGNAL_CASE_FLOW_VISUALS_CREATED] || null;
  const manualRecord = workflowState?.manualCompleteByTag?.CASE_FLOW_VISUALS || null;
  const signalCurrent = !!signalRecord?.completedAtUtc && Number(signalRecord.revision) === revision;
  const manualCurrent = !!manualRecord?.completedAtUtc;
  const reviewIsCurrent = signalCurrent || manualCurrent;
  const completedAtUtc = signalRecord?.completedAtUtc || manualRecord?.completedAtUtc || '';
  const source = signalCurrent ? 'signal' : manualCurrent ? 'manual' : '';
  const rows = buildCaseFlowVisualsPlaceholderRows({
    caseDisplayName: options.caseDisplayName,
    caseDisplayId: options.caseDisplayId,
    caseDataRevision: revision,
    reviewIsCurrent,
    completedAtUtc,
    source
  });
  const currentBasisHash = `case-flow-visuals-placeholder-${caseBuddyHashString(JSON.stringify({
    renderer: CASE_FLOW_VISUALS_PLACEHOLDER_RENDERER_VERSION,
    caseId: normalizeCell(options.caseDisplayId),
    caseName: normalizeCell(options.caseDisplayName),
    revision
  }))}`;
  return {
    rendererVersion: CASE_FLOW_VISUALS_PLACEHOLDER_RENDERER_VERSION,
    currentBasisHash,
    caseDataRevision: revision,
    reviewIsCurrent,
    signalCurrent,
    manualCurrent,
    completedAtUtc,
    source,
    rows,
    summary: reviewIsCurrent
      ? `Case flow visuals are marked complete${completedAtUtc ? ` (${safeDateLabel(completedAtUtc)})` : ''}.`
      : 'Case flow visuals can be generated inside Case Buddy Web App with the Tool87-style visual builder.'
  };
}

export function buildCaseFlowVisualsPlaceholderRows(options = {}) {
  return [
    ['Case Flow Visuals Guidance', options.caseDisplayName || '', '', '', '', '', '', '', '', 'Tool87-style in-app visual package. Main Data is not modified.'],
    ['Case ID', options.caseDisplayId || '', 'Revision', options.caseDataRevision || 0, 'Renderer', CASE_FLOW_VISUALS_PLACEHOLDER_RENDERER_VERSION, 'Marked Complete', options.reviewIsCurrent ? 'Yes' : 'No'],
    [],
    ['Current visual package guidance'],
    ['Option', 'Instructions', 'Status'],
    ['In-app visual builder', 'Open Create case flow visuals to preview each main flow and download the SVG, PNG, or JPG visual package.', 'Available inside Case Buddy Web App'],
    ['Recommended setup', 'Address labels, swap/bridge highlights, WayFinder-style merge, repeated transaction merge, trunk wrapping, and overlap cleanup are enabled by default.', 'Applied automatically'],
    ['Manual completion', 'After the investigator has reviewed or downloaded the visual package, use Mark Visuals Done if the package was not automatically marked complete.', options.reviewIsCurrent ? `Completed by ${options.source || 'workflow'}` : 'Not marked complete']
  ];
}

export function buildCasePackageTitle(inputs = {}) {
  const caseId = normalizeCell(inputs.caseDisplayId || inputs.caseId);
  const caseName = normalizeCell(inputs.caseDisplayName || inputs.caseName);
  if (caseId && caseName && caseName !== caseId) return `${caseId} - ${caseName} Full Report Package`;
  if (caseId) return `${caseId} Full Report Package`;
  if (caseName) return `${caseName} Full Report Package`;
  return 'Case Buddy Full Report Package';
}

export function normalizeCasePackageAddendumText(value = '') {
  const safe = normalizeCasePackageLineBreaks(value).trim();
  if (!safe) return '';
  return safe.replace(/\nExchange:/g, '\n\nExchange:');
}

export function normalizeCasePackageAddendumHtml(htmlFragment = '') {
  const safe = String(htmlFragment || '').trim();
  if (!safe) return '';
  return safe.replace(
    /<div style="height: 18px;"><\/div><div style="font-family: Arial; color: black;">/g,
    '<br><br><div style="height: 18px;"></div><div style="font-family: Arial; color: black;">'
  );
}

export function buildCaseFullReportPackageBasisHash(inputs = {}) {
  return `case-full-report-package-${caseBuddyHashString(JSON.stringify({
    renderer: CASE_FULL_REPORT_PACKAGE_RENDERER_VERSION,
    caseId: normalizeCell(inputs.caseDisplayId || inputs.caseId),
    caseName: normalizeCell(inputs.caseDisplayName || inputs.caseName),
    revision: Number(inputs.caseDataRevision) || 0,
    reportBodyBasis: inputs.reportBodyState?.currentBasisHash || '',
    reportBodyReviewed: !!inputs.reportBodyState?.reviewIsCurrent,
    conclusionBasis: inputs.conclusionState?.currentBasisHash || '',
    whatWentWhereBasis: inputs.whatWentWhereState?.currentBasisHash || '',
    addendumBasis: inputs.addendumState?.currentReviewBasisHash || '',
    addendumReviewed: !!inputs.addendumState?.reviewIsCurrent,
    visualsBasis: inputs.visualsState?.currentBasisHash || '',
    visualsRenderer: CASE_FLOW_VISUALS_RENDERER_VERSION,
    visualsReviewed: !!inputs.visualsState?.reviewIsCurrent,
    embeddedVisualsEnabled: inputs.embedFlowVisualsInReport !== false,
    visualOptions: casePackageNormalizeVisualOptions(inputs.visualOptions || {}),
    suspiciousRows: casePackageRowsToTsv(inputs.suspiciousAddressRows || []),
    exchangeRows: casePackageRowsToTsv(inputs.exchangeAddressRows || []),
    dormantRows: casePackageRowsToTsv(inputs.dormantAddressRows || []),
    addressLegendRows: keepFirstCasePackageTsvColumns(casePackageRowsToTsv(inputs.addressLegendRows || []), 2),
    duplicateRows: casePackageRowsToTsv(inputs.duplicateHashRows || [])
  }))}`;
}

export function buildCaseFullReportPackageState(inputs = {}) {
  const revision = Number(inputs.caseDataRevision) || 0;
  const reportBodyIsCurrent = !!inputs.reportBodyState?.canBuild && !!inputs.reportBodyState?.buildIsCurrent;
  const reportBodyReviewIsCurrent = reportBodyIsCurrent && !!inputs.reportBodyState?.reviewIsCurrent;
  const addendumIsCurrent = !!inputs.addendumState?.canBuild;
  const addendumReviewIsCurrent = addendumIsCurrent && !!inputs.addendumState?.reviewIsCurrent;
  const visualsCanBuild = !!inputs.visualsState?.canBuild;
  const visualCount = Number(inputs.visualsState?.flowCount) || 0;
  const visualsReviewIsCurrent = !!inputs.visualsState?.reviewIsCurrent;
  const missing = [];
  if (!reportBodyIsCurrent) missing.push('Report Body is not current.');
  else if (!reportBodyReviewIsCurrent) missing.push('Report Body is current but has not been reviewed.');
  if (!addendumIsCurrent) missing.push('Addendum is not current.');
  else if (!addendumReviewIsCurrent) missing.push('Addendum is current but has not been reviewed.');
  const isReady = reportBodyIsCurrent && reportBodyReviewIsCurrent && addendumIsCurrent && addendumReviewIsCurrent;
  const currentBasisHash = buildCaseFullReportPackageBasisHash(inputs);
  const review = inputs.workflowState?.fullReportPackageReview || null;
  const reviewIsCurrent = isReady && !!review?.reviewedAtUtc && review.basisHash === currentBasisHash && Number(review.revision) === revision;
  const deliverables = Array.isArray(inputs.deliverableViews) ? inputs.deliverableViews : [];
  const deliverableCounts = {
    total: deliverables.length,
    current: deliverables.filter(item => item.status === 'current').length,
    available: deliverables.filter(item => item.available).length,
    stale: deliverables.filter(item => item.status === 'stale').length,
    reviewed: deliverables.filter(item => item.reviewed).length
  };
  return {
    rendererVersion: CASE_FULL_REPORT_PACKAGE_RENDERER_VERSION,
    caseId: normalizeCell(inputs.caseDisplayId || inputs.caseId),
    caseName: normalizeCell(inputs.caseDisplayName || inputs.caseName),
    caseDataRevision: revision,
    mainRowCount: Number(inputs.caseSummary?.mainEntries) || 0,
    rawRowCount: Number(inputs.caseSummary?.rawEntries) || 0,
    reportBodyIsCurrent,
    reportBodyReviewIsCurrent,
    addendumIsCurrent,
    addendumReviewIsCurrent,
    visualsCanBuild,
    visualCount,
    visualsReviewIsCurrent,
    isReady,
    canBuild: isReady,
    currentBasisHash,
    reviewedAtUtc: normalizeCell(review?.reviewedAtUtc),
    reviewIsCurrent,
    deliverableCounts,
    missingReasons: missing,
    missingReason: missing.join(' '),
    summary: isReady
      ? reviewIsCurrent
        ? 'Full report package is ready and has been marked reviewed/copied for the current case data.'
        : 'Full report package is ready to preview, copy, or download. Copying places rich HTML and a plain text fallback on the clipboard when the browser allows it.'
      : `Full report package is not ready yet. ${missing.join(' ')}`.trim()
  };
}

export function buildCaseCompletionSummaryText(inputs = {}, state = null) {
  const packageState = state || buildCaseFullReportPackageState(inputs);
  const lines = [];
  lines.push('Case Completion Summary');
  lines.push('');
  lines.push(`Case: ${packageState.caseId || packageState.caseName || 'Untitled case'}`);
  if (packageState.caseId && packageState.caseName && packageState.caseId !== packageState.caseName) lines.push(`Case name: ${packageState.caseName}`);
  lines.push(`Case data revision: ${packageState.caseDataRevision}`);
  lines.push(`Main entries: ${packageState.mainRowCount}`);
  lines.push(`Raw entries: ${packageState.rawRowCount}`);
  lines.push(`Destination addresses: ${Number(inputs.caseSummary?.destinationAddressCount) || 0}`);
  lines.push(`Missing path labels: ${Number(inputs.caseSummary?.missingPathLabels) || 0}`);
  lines.push('');
  lines.push('Reviewed package prerequisites:');
  lines.push(`- Report Body current: ${packageState.reportBodyIsCurrent ? 'Yes' : 'No'}`);
  lines.push(`- Report Body reviewed: ${packageState.reportBodyReviewIsCurrent ? 'Yes' : 'No'}`);
  lines.push(`- Addendum current: ${packageState.addendumIsCurrent ? 'Yes' : 'No'}`);
  lines.push(`- Addendum reviewed: ${packageState.addendumReviewIsCurrent ? 'Yes' : 'No'}`);
  lines.push(`- Case flow visuals available: ${packageState.visualsCanBuild ? `Yes (${packageState.visualCount || 0})` : 'No'}`);
  lines.push(`- Case flow visuals marked done: ${packageState.visualsReviewIsCurrent ? 'Yes' : 'No'}${packageState.visualsReviewIsCurrent ? '' : packageState.visualsCanBuild ? ' (rich package can embed generated visuals; full-size package still recommended)' : ' (visuals still need in-app review/export)'}`);
  lines.push('');
  lines.push('Deliverable snapshot:');
  lines.push(`- ${packageState.deliverableCounts.current}/${packageState.deliverableCounts.total} deliverables current`);
  lines.push(`- ${packageState.deliverableCounts.reviewed}/${packageState.deliverableCounts.total} deliverables marked reviewed`);
  lines.push(`- ${packageState.deliverableCounts.stale} stale deliverable${packageState.deliverableCounts.stale === 1 ? '' : 's'}`);
  if (packageState.missingReason) {
    lines.push('');
    lines.push(`Missing before shipping: ${packageState.missingReason}`);
  }
  lines.push('');
  lines.push(packageState.summary);
  return lines.join('\n').trim();
}

export function createCasePackageOutputBuffer() {
  return { plain: '', html: '', sections: [] };
}

export function appendCasePackageTitle(output, title = '') {
  const safeTitle = normalizeCell(title) || 'Case Buddy Full Report Package';
  output.plain += `${safeTitle}\n${'='.repeat(safeTitle.length)}\n\n`;
  output.html += `<h1>${casePackageHtmlEncode(safeTitle)}</h1>`;
}

export function appendCasePackageSectionHeader(output, title = '') {
  const safeTitle = normalizeCell(title) || 'Section';
  output.plain += `\n${safeTitle}\n${'-'.repeat(safeTitle.length)}\n`;
  output.html += `<h2>${casePackageHtmlEncode(safeTitle)}</h2>`;
}

export function appendCasePackageTextSection(output, title = '', text = '') {
  const safeText = normalizeCasePackageLineBreaks(text).trim() || 'Not available.';
  appendCasePackageSectionHeader(output, title);
  output.plain += `${safeText}\n\n`;
  output.html += casePackagePlainTextToHtml(safeText);
  output.sections.push({ title: normalizeCell(title), kind: 'text', rowCount: 0, available: !!safeText });
}

export function appendCasePackageRichSection(output, title = '', plainText = '', htmlFragment = '') {
  const safeText = normalizeCasePackageLineBreaks(plainText).trim() || 'Not available.';
  appendCasePackageSectionHeader(output, title);
  output.plain += `${safeText}\n\n`;
  output.html += normalizeCell(htmlFragment) ? String(htmlFragment || '').trim() : casePackagePlainTextToHtml(safeText);
  output.sections.push({ title: normalizeCell(title), kind: 'rich', rowCount: 0, available: !!safeText });
}

export function appendCasePackageTsvSection(output, title = '', tsv = '', optional = false) {
  const safeTsv = normalizeCasePackageLineBreaks(tsv).trim();
  if (optional && !casePackageHasMeaningfulTsvRows(safeTsv)) return false;
  const printableTsv = safeTsv || 'Not available.';
  appendCasePackageSectionHeader(output, title);
  output.plain += `${normalizeEscapedCasePackageCellBreaks(printableTsv)}\n\n`;
  output.html += printableTsv.includes('\t') ? casePackageTsvToHtmlTable(printableTsv) : casePackagePlainTextToHtml(printableTsv);
  const rowCount = printableTsv.includes('\t') ? Math.max(0, printableTsv.split('\n').filter(line => line.trim()).length - 1) : 0;
  output.sections.push({ title: normalizeCell(title), kind: 'tsv', rowCount, available: !!safeTsv });
  return true;
}


export function casePackageWwwTruncateNumber(value = 0, decimals = 2) {
  const numeric = Number(value);
  const places = Math.max(0, Number(decimals) || 0);
  if (!Number.isFinite(numeric)) return null;
  const factor = 10 ** places;
  const truncated = Math.trunc(Math.abs(numeric) * factor + 1e-9) / factor;
  return numeric < 0 ? -truncated : truncated;
}

export function casePackageWwwFormatNumberNoRounding(value = 0, decimals = 2) {
  const truncated = casePackageWwwTruncateNumber(value, decimals);
  if (!Number.isFinite(truncated)) return '';
  return truncated.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

export function casePackageWwwFormatUsdNoRounding(value = 0) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  const absolute = casePackageWwwFormatNumberNoRounding(Math.abs(numeric), 2);
  return numeric < 0 ? `-$${absolute}` : `$${absolute}`;
}

export function casePackageWwwIsStableToken(token = '') {
  return CASE_PACKAGE_WWW_STABLE_TOKEN_SET.has(normalizeCell(token).toUpperCase());
}

export function casePackageWwwFormatTokenAmountNoRounding(value = 0, token = '') {
  const normalizedToken = normalizeCell(token);
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || !normalizedToken) return '';
  const decimals = casePackageWwwIsStableToken(normalizedToken) ? 2 : 4;
  return `${casePackageWwwFormatNumberNoRounding(numeric, decimals)} ${normalizedToken}`;
}

export function casePackageWwwTokenTotalsToEntries(tokenTotals = null, fallbackText = '') {
  if (tokenTotals instanceof Map) {
    return Array.from(tokenTotals.entries())
      .map(([token, amount]) => ({ token: normalizeCell(token), amount: Number(amount) }))
      .filter(row => row.token && Number.isFinite(row.amount));
  }
  if (Array.isArray(tokenTotals)) {
    return tokenTotals
      .map((item) => Array.isArray(item)
        ? { token: normalizeCell(item[0]), amount: Number(item[1]) }
        : { token: normalizeCell(item?.token || item?.symbol || item?.name), amount: Number(item?.amount || item?.value) })
      .filter(row => row.token && Number.isFinite(row.amount));
  }
  if (tokenTotals && typeof tokenTotals === 'object') {
    return Object.entries(tokenTotals)
      .map(([token, amount]) => ({ token: normalizeCell(token), amount: Number(amount) }))
      .filter(row => row.token && Number.isFinite(row.amount));
  }
  return normalizeCell(fallbackText)
    .split(/,\s*/)
    .map((part) => {
      const match = normalizeCell(part).match(/^(-?[$0-9,.]+(?:\.\d+)?)\s+(.+)$/);
      if (!match) return null;
      return { token: normalizeCell(match[2]), amount: Number(match[1].replace(/[$,]/g, '')) };
    })
    .filter(row => row && row.token && Number.isFinite(row.amount));
}

export function casePackageWwwBuildTokenText(tokenTotals = null, fallbackText = '') {
  const entries = casePackageWwwTokenTotalsToEntries(tokenTotals, fallbackText)
    .sort((left, right) => {
      const leftStable = casePackageWwwIsStableToken(left.token) ? 1 : 0;
      const rightStable = casePackageWwwIsStableToken(right.token) ? 1 : 0;
      return leftStable - rightStable || right.amount - left.amount || left.token.localeCompare(right.token);
    });
  const text = entries
    .map(row => casePackageWwwFormatTokenAmountNoRounding(row.amount, row.token))
    .filter(Boolean)
    .join(', ');
  return text || normalizeCell(fallbackText);
}

export function casePackageWwwNormalizeSummaryRows(rows = []) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const category = normalizeCell(row?.category);
      const entity = normalizeCell(row?.entity);
      const usdTotal = Number(row?.usdTotal);
      const unresolvedCount = Number(row?.unresolvedCount) || 0;
      const tokensText = casePackageWwwBuildTokenText(row?.tokenTotals, row?.tokensText || '');
      return {
        ...row,
        category,
        entity,
        usdTotal: Number.isFinite(usdTotal) ? usdTotal : 0,
        unresolvedCount,
        usdDisplayNoRounding: Number.isFinite(usdTotal) ? casePackageWwwFormatUsdNoRounding(usdTotal) : normalizeCell(row?.usdDisplay),
        tokensTextNoRounding: tokensText
      };
    })
    .filter(row => row.category || row.entity || row.usdTotal || row.tokensTextNoRounding || row.unresolvedCount);
}

export function casePackageWwwBuildEntityTableRows(whatWentWhereState = {}) {
  const rows = casePackageWwwNormalizeSummaryRows(whatWentWhereState.entitySummaryRows || []);
  const primaryRows = rows.filter(row => CASE_PACKAGE_WWW_ENTITY_TABLE_CATEGORIES.includes(row.category) && row.entity);
  const selectedRows = primaryRows.length
    ? primaryRows
    : rows.filter(row => row.entity && !['Dormant Wallet', 'Unknown', 'Lost/Dismissed'].includes(row.category));
  return selectedRows
    .sort((left, right) => right.usdTotal - left.usdTotal || left.entity.localeCompare(right.entity))
    .map(row => ({
      entity: row.entity || row.category || 'Unknown entity',
      usdTotal: row.usdTotal,
      usdText: row.usdDisplayNoRounding || casePackageWwwFormatUsdNoRounding(row.usdTotal),
      tokenText: row.tokensTextNoRounding ? `(${row.tokensTextNoRounding})` : '',
      category: row.category,
      unresolvedCount: row.unresolvedCount || 0
    }));
}

export function casePackageWwwGetCategoryRows(whatWentWhereState = {}) {
  const rows = casePackageWwwNormalizeSummaryRows(whatWentWhereState.categorySummaryRows || []);
  const byCategory = new Map(rows.map(row => [row.category, row]));
  return byCategory;
}

export function casePackageWwwBuildAdditionalSummarySentences(whatWentWhereState = {}) {
  const byCategory = casePackageWwwGetCategoryRows(whatWentWhereState);
  const sentences = [];
  const dormant = byCategory.get('Dormant Wallet');
  if (dormant && dormant.usdTotal > 0) {
    sentences.push(`Additionally, some funds were traced to dormant addresses: ${casePackageWwwFormatUsdNoRounding(dormant.usdTotal)}.`);
  }
  const unknown = byCategory.get('Unknown');
  if (unknown && unknown.usdTotal > 0) {
    sentences.push(`Some funds were traced to unknown entities or entities with features of unattributed exchange hot wallets or service addresses: ${casePackageWwwFormatUsdNoRounding(unknown.usdTotal)}.`);
  }
  const otherSummaries = CASE_PACKAGE_WWW_OTHER_SUMMARY_CATEGORIES
    .map(category => byCategory.get(category))
    .filter(row => row && row.usdTotal > 0)
    .map((row) => {
      const tokenText = row.tokensTextNoRounding ? ` (${row.tokensTextNoRounding})` : '';
      return `${row.category}: ${casePackageWwwFormatUsdNoRounding(row.usdTotal)}${tokenText}`;
    });
  if (otherSummaries.length) {
    sentences.push(`Funds traced to other entity types: ${otherSummaries.join('; ')}.`);
  }
  if (!sentences.length && Number(whatWentWhereState?.overallUsdTotal) > 0) {
    sentences.push(`Total traced value: ${casePackageWwwFormatUsdNoRounding(whatWentWhereState.overallUsdTotal)}.`);
  }
  return sentences;
}

export function casePackageWwwTabsAfterEntity(entity = '', longestEntityLength = 0) {
  const length = normalizeCell(entity).length;
  const tabStops = Math.max(1, Math.ceil((Math.max(longestEntityLength, 12) - length + 1) / 8) + 1);
  return '\t'.repeat(tabStops);
}

export function buildCasePackageWhatWentWhereSummaryPayload(whatWentWhereState = {}, whatWentWhereRows = []) {
  const state = whatWentWhereState || {};
  const entityRows = casePackageWwwBuildEntityTableRows(state);
  const overallUsd = Number(state.overallUsdTotal);
  const tableTotal = entityRows.reduce((sum, row) => sum + (Number(row.usdTotal) || 0), 0);
  const totalValue = entityRows.length ? tableTotal : (Number.isFinite(overallUsd) ? overallUsd : 0);
  const additionalSentences = casePackageWwwBuildAdditionalSummarySentences(state);
  const longestEntityLength = Math.max(0, ...entityRows.map(row => normalizeCell(row.entity).length));
  const plainLines = ['USD/Entity Table', '', 'Entity\tUSD Value\tToken Value'];
  if (entityRows.length) {
    entityRows.forEach((row) => {
      plainLines.push(`${row.entity}${casePackageWwwTabsAfterEntity(row.entity, longestEntityLength)}${row.usdText}\t${row.tokenText}`.trimEnd());
    });
    plainLines.push(`${'-'.repeat(Math.max(24, longestEntityLength + 28))}`);
    plainLines.push(`Total:${casePackageWwwTabsAfterEntity('Total:', longestEntityLength)}${casePackageWwwFormatUsdNoRounding(totalValue)}`.trimEnd());
  } else {
    plainLines.push('No resolved exchange/service entity rows were available for this What Went Where summary.');
    if (Number.isFinite(overallUsd) && overallUsd > 0) plainLines.push(`Total:\t${casePackageWwwFormatUsdNoRounding(overallUsd)}`);
  }
  if (additionalSentences.length) {
    plainLines.push('', additionalSentences.join(' '));
  }
  const bodyRows = entityRows.length
    ? entityRows.map((row, index) => `<tr${index === entityRows.length - 1 ? ' class="case-package-www-last-entity-row"' : ''}><td>${casePackageHtmlEncode(row.entity)}</td><td>${casePackageHtmlEncode(row.usdText)}</td><td>${casePackageHtmlEncode(row.tokenText)}</td></tr>`).join('')
    : '<tr><td colspan="3"><em>No resolved exchange/service entity rows were available for this What Went Where summary.</em></td></tr>';
  const summaryParagraph = additionalSentences.length
    ? `<p class="case-package-www-summary-note">${casePackageHtmlEncode(additionalSentences.join(' '))}</p>`
    : '';
  const totalHtml = entityRows.length || (Number.isFinite(overallUsd) && overallUsd > 0)
    ? `<tr class="case-package-www-total-row"><td>Total:</td><td>${casePackageHtmlEncode(casePackageWwwFormatUsdNoRounding(totalValue))}</td><td></td></tr>`
    : '';
  const htmlFragment = `<div class="case-package-www-summary"><h3>USD/Entity Table</h3><table class="case-package-www-summary-table"><thead><tr><th>Entity</th><th>USD Value</th><th>Token Value</th></tr></thead><tbody>${bodyRows}${totalHtml}</tbody></table>${summaryParagraph}</div>`;
  const rowCount = entityRows.length || Math.max(0, (Array.isArray(whatWentWhereRows) ? whatWentWhereRows.length : 0) - 1);
  return {
    plainText: plainLines.join('\n').trim(),
    htmlFragment,
    rowCount,
    entityRows,
    additionalSentences,
    totalValue,
    totalText: casePackageWwwFormatUsdNoRounding(totalValue)
  };
}

export function appendCasePackageWhatWentWhereSummarySection(output, whatWentWhereState = {}, whatWentWhereRows = []) {
  const summary = buildCasePackageWhatWentWhereSummaryPayload(whatWentWhereState, whatWentWhereRows);
  appendCasePackageSectionHeader(output, 'What Went Where');
  output.plain += `${summary.plainText}\n\n`;
  output.html += summary.htmlFragment;
  output.sections.push({
    title: 'What Went Where',
    kind: 'summary',
    rowCount: summary.rowCount || 0,
    available: !!summary.plainText,
    entityCount: summary.entityRows.length,
    summaryTotal: summary.totalText
  });
  return summary;
}

export function buildCaseFullReportPackagePayload(inputs = {}) {
  const state = inputs.state || buildCaseFullReportPackageState(inputs);
  const output = createCasePackageOutputBuffer();
  appendCasePackageTitle(output, buildCasePackageTitle(inputs));
  appendCasePackageTextSection(output, 'Case Completion Summary', buildCaseCompletionSummaryText(inputs, state));

  const reportBodyPackage = buildCasePackageReportBodyWithEmbeddedVisuals(
    inputs.reportBodyState || {},
    inputs.visualsState || {},
    {
      embedFlowVisuals: inputs.embedFlowVisualsInReport !== false,
      visualOptions: inputs.visualOptions || {}
    }
  );
  appendCasePackageRichSection(output, 'Report Body', reportBodyPackage.plainText, reportBodyPackage.htmlFragment);
  if (output.sections.length) {
    const reportSection = output.sections[output.sections.length - 1];
    reportSection.embeddedVisualCount = reportBodyPackage.embeddedVisualCount || 0;
    reportSection.missingVisualCount = reportBodyPackage.missingVisualCount || 0;
  }

  const conclusionText = normalizeCasePackageLineBreaks(inputs.conclusionText || inputs.conclusionState?.selectedText || inputs.conclusionState?.generatedText || '').trim() || 'No current conclusion text is available.';
  appendCasePackageTextSection(output, 'Conclusion', conclusionText);

  appendCasePackageWhatWentWhereSummarySection(output, inputs.whatWentWhereState || {}, inputs.whatWentWhereRows || []);
  appendCasePackageTsvSection(output, 'Suspicious Address Table', casePackageRowsToTsv(inputs.suspiciousAddressRows || []), true);
  appendCasePackageTsvSection(output, 'Exchange Address Table', casePackageRowsToTsv(inputs.exchangeAddressRows || []), true);
  appendCasePackageTsvSection(output, 'Dormant Address Table', casePackageRowsToTsv(inputs.dormantAddressRows || []), true);
  appendCasePackageTsvSection(output, 'Address Legend', keepFirstCasePackageTsvColumns(casePackageRowsToTsv(inputs.addressLegendRows || []), 2), true);

  const addendumText = normalizeCasePackageAddendumText(inputs.addendumState?.generatedAddendumText || '') || 'No current addendum output is available.';
  const addendumHtml = normalizeCasePackageAddendumHtml(inputs.addendumState?.structuredHtmlFragment || '');
  appendCasePackageRichSection(output, 'Addendum / Path Endings', addendumText, addendumHtml);

  appendCasePackageTsvSection(output, 'Appendix - Duplicate Hash Table', casePackageRowsToTsv(inputs.duplicateHashRows || []), true);

  const htmlFragment = output.html;
  const htmlDocument = buildCasePackageHtmlDocument(htmlFragment);
  const plainText = output.plain.trimEnd();
  return {
    rendererVersion: CASE_FULL_REPORT_PACKAGE_RENDERER_VERSION,
    caseId: state.caseId,
    isReady: !!state.isReady,
    basisHash: state.currentBasisHash || '',
    summary: state.summary || '',
    missingReason: state.missingReason || '',
    plainText,
    previewText: buildCasePackagePreviewText(plainText),
    htmlFragment,
    htmlDocument,
    sections: output.sections,
    embeddedVisualCount: reportBodyPackage.embeddedVisualCount || 0,
    missingVisualCount: reportBodyPackage.missingVisualCount || 0,
    plainTextLength: plainText.length,
    htmlLength: htmlDocument.length,
    state
  };
}

export function buildCaseFullReportPackagePreviewRows(payload = {}, state = null) {
  const packageState = state || payload.state || {};
  const sections = Array.isArray(payload.sections) ? payload.sections : [];
  return [
    ['Case Buddy Full Report Package Preview', payload.caseId || packageState.caseId || '', '', '', '', '', '', '', '', 'Preview/audit only. Copy/download produces the complete report package.'],
    ['Renderer', payload.rendererVersion || CASE_FULL_REPORT_PACKAGE_RENDERER_VERSION, 'Basis', payload.basisHash || packageState.currentBasisHash || '', 'Ready', payload.isReady ? 'Yes' : 'No', 'Review Current', packageState.reviewIsCurrent ? 'Yes' : 'No'],
    ['Plain Text Length', payload.plainTextLength || 0, 'HTML Length', payload.htmlLength || 0, 'Missing', payload.missingReason || '', 'Embedded Visuals', payload.embeddedVisualCount || 0],
    [],
    ['Sections'],
    ['Section', 'Kind', 'Rows', 'Available', 'Embedded Visuals'],
    ...sections.map(section => [section.title, section.kind, section.rowCount || 0, section.available ? 'Yes' : 'No', section.embeddedVisualCount || '']),
    [],
    ['Plain Text Preview'],
    ...String(payload.previewText || '').split('\n').map(line => [line])
  ];
}

export function buildCaseFullReportPackageAuditRows(payload = {}, actionLabel = 'Full report package review') {
  const state = payload.state || {};
  return [
    ['Case Buddy Full Report Package Audit', '', '', '', '', '', '', '', '', 'Audit only. Main Data and Raw Data were not modified.'],
    ['Action', actionLabel, 'Ready', state.isReady ? 'Yes' : 'No', 'Review Current', state.reviewIsCurrent ? 'Yes' : 'No', 'Basis', state.currentBasisHash || payload.basisHash || ''],
    ['Report Body Current', state.reportBodyIsCurrent ? 'Yes' : 'No', 'Report Body Reviewed', state.reportBodyReviewIsCurrent ? 'Yes' : 'No', 'Addendum Current', state.addendumIsCurrent ? 'Yes' : 'No', 'Addendum Reviewed', state.addendumReviewIsCurrent ? 'Yes' : 'No'],
    ['Visuals Marked Done', state.visualsReviewIsCurrent ? 'Yes' : 'No', 'Embedded Visuals', payload.embeddedVisualCount || 0, 'Plain Text Length', payload.plainTextLength || 0, 'HTML Length', payload.htmlLength || 0, 'Renderer', payload.rendererVersion || CASE_FULL_REPORT_PACKAGE_RENDERER_VERSION],
    ['Summary', state.summary || payload.summary || ''],
    [],
    ['Sections'],
    ['Section', 'Kind', 'Rows', 'Available', 'Embedded Visuals'],
    ...(Array.isArray(payload.sections) ? payload.sections : []).map(section => [section.title, section.kind, section.rowCount || 0, section.available ? 'Yes' : 'No', section.embeddedVisualCount || ''])
  ];
}

export function markCaseFullReportPackageReviewRecord(workflowState = {}, payload = {}, options = {}) {
  const reviewedAtUtc = options.reviewedAtUtc || new Date().toISOString();
  return {
    ...(workflowState || {}),
    fullReportPackageReview: {
      ...(workflowState?.fullReportPackageReview || {}),
      reviewedAtUtc,
      copiedAtUtc: options.copied ? reviewedAtUtc : (workflowState?.fullReportPackageReview?.copiedAtUtc || ''),
      downloadedAtUtc: options.downloaded ? reviewedAtUtc : (workflowState?.fullReportPackageReview?.downloadedAtUtc || ''),
      revision: Number(options.caseDataRevision) || Number(payload.state?.caseDataRevision) || 0,
      basisHash: payload.basisHash || payload.state?.currentBasisHash || '',
      rendererVersion: payload.rendererVersion || CASE_FULL_REPORT_PACKAGE_RENDERER_VERSION,
      plainTextLength: payload.plainTextLength || 0,
      htmlLength: payload.htmlLength || 0,
      source: options.source || ''
    },
    signalCompleteByCode: {
      ...(workflowState?.signalCompleteByCode || {}),
      [WORKFLOW_SIGNAL_FULL_REPORT_PACKAGE_REVIEWED]: {
        completedAtUtc: reviewedAtUtc,
        revision: Number(options.caseDataRevision) || Number(payload.state?.caseDataRevision) || 0,
        source: options.source || 'full_report_package'
      }
    }
  };
}
